/**
 * `/preferences` — screen 18.
 *
 * Per-Account and persisted: interface language, navigation mode, workspace layout, density. The
 * shell genuinely supports both navigations, which is why this is a preference rather than a
 * breakpoint (11-screens.md).
 *
 * ## Why the language card explains four things
 *
 * Conflating the four language axes is *the* common mistake in this area (12-localization.md), and
 * this screen is where somebody forms their belief about what "language" means here. Setting the
 * interface to Spanish and then finding an English worship aid printed would look like a bug; being
 * told beforehand that exports carry their own language makes it the feature it is.
 *
 * So the aside names all four and says plainly which one this control governs.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { LAUNCH_LANGUAGES, type LaunchLanguage } from '@pius/contracts';

import type { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import { setLanguage } from '../../i18n';
import { useAuth, useSignOut } from '../auth/useAuth';
import { fetchPreferences, savePreferences, type Preferences } from './api';
import './preferences.css';

const LANGUAGE_NAMES: Record<LaunchLanguage, string> = { en: 'English', es: 'Español' };

export function PreferencesScreen(): React.JSX.Element {
  const { t } = useTranslation('preferences');
  const queryClient = useQueryClient();
  const { account } = useAuth();
  const signOut = useSignOut();
  const [copied, setCopied] = useState(false);

  const preferences = useQuery({ queryKey: ['preferences'], queryFn: fetchPreferences });

  const save = useMutation({
    mutationFn: (patch: Partial<Preferences>) => savePreferences(patch),
    onSuccess: async (next) => {
      queryClient.setQueryData(['preferences'], next);
      // Applied immediately rather than on the next load: a language control that takes effect
      // later reads as broken.
      await setLanguage(next.interface_language);
    },
  });

  const error = [preferences.error, save.error].find((e) => e instanceof Error) as
    ApiError | undefined;

  const current = preferences.data;

  const handleCopyId = async (): Promise<void> => {
    if (!account?.id) return;
    try {
      await navigator.clipboard.writeText(account.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <div className="prefs">
      <header className="prefs__head">
        <div>
          <h1 className="prefs__title">{t('title')}</h1>
          <p className="prefs__lede">{t('lede')}</p>
        </div>
      </header>

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      <div className="prefs__grid">
        <div className="prefs__main">
          {account ? (
            <section className="prefs__card" style={{ borderLeft: '3px solid var(--blue)' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: 14,
                }}
              >
                <div>
                  <p className="prefs__eyebrow">Your Account</p>
                  <h2
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: 22,
                      margin: '2px 0 6px',
                      fontWeight: 400,
                    }}
                  >
                    {account.display_name}
                  </h2>
                  <p style={{ fontSize: 13.5, color: 'var(--muted-strong)', margin: 0 }}>
                    {account.email}
                  </p>
                  <div
                    style={{
                      marginTop: 8,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 11,
                        color: 'var(--muted)',
                      }}
                    >
                      Account ID: <strong style={{ color: 'var(--ink)' }}>{account.id}</strong>
                    </span>
                    <button
                      type="button"
                      className="button button--ghost"
                      style={{ padding: '2px 6px', fontSize: 10 }}
                      onClick={handleCopyId}
                    >
                      {copied ? 'Copied!' : 'Copy ID'}
                    </button>
                  </div>
                  {account.is_site_administrator ? (
                    <div style={{ marginTop: 8 }}>
                      <span className="pill pill--active">Site Administrator</span>
                    </div>
                  ) : null}
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Link className="button button--secondary" to="/security">
                    Security & MFA →
                  </Link>
                  <button
                    type="button"
                    className="button button--ghost"
                    disabled={signOut.isPending}
                    onClick={() => signOut.mutate()}
                  >
                    {signOut.isPending ? 'Signing out…' : 'Sign out'}
                  </button>
                </div>
              </div>
            </section>
          ) : (
            <section className="prefs__card">
              <p className="prefs__eyebrow">Account</p>
              <p className="prefs__fine">
                You are currently browsing anonymously. Sign in to link your liturgies, customary
                policies, and calendar sync.
              </p>
              <div style={{ marginTop: 12 }}>
                <Link className="button button--primary" to="/login">
                  Sign in →
                </Link>
              </div>
            </section>
          )}

          <section className="prefs__card">
            <p className="prefs__eyebrow">{t('language.eyebrow')}</p>
            <p className="prefs__fine">{t('language.note')}</p>

            <div className="prefs__choices">
              {LAUNCH_LANGUAGES.map((language) => (
                <button
                  key={language}
                  type="button"
                  lang={language}
                  aria-pressed={current?.interface_language === language}
                  className={`prefs__choice${
                    current?.interface_language === language ? ' prefs__choice--on' : ''
                  }`}
                  disabled={save.isPending}
                  onClick={() => save.mutate({ interface_language: language })}
                >
                  {LANGUAGE_NAMES[language]}
                </button>
              ))}
            </div>
          </section>

          <Choice
            eyebrow={t('navigation.eyebrow')}
            value={current?.navigation}
            disabled={save.isPending}
            onChange={(navigation) => save.mutate({ navigation })}
            options={[
              { value: 'top_bar', label: t('navigation.bar'), note: t('navigation.bar_note') },
              { value: 'left_rail', label: t('navigation.rail'), note: t('navigation.rail_note') },
            ]}
          />

          <Choice
            eyebrow={t('layout.eyebrow')}
            value={current?.workspace_layout}
            disabled={save.isPending}
            onChange={(workspace_layout) => save.mutate({ workspace_layout })}
            options={[
              {
                value: 'comfortable',
                label: t('layout.comfortable'),
                note: t('layout.comfortable_note'),
              },
              { value: 'compact', label: t('layout.compact'), note: t('layout.compact_note') },
            ]}
          />

          <Choice
            eyebrow={t('density.eyebrow')}
            value={current?.density}
            disabled={save.isPending}
            note={t('density.note')}
            onChange={(density) => save.mutate({ density })}
            options={[
              { value: 'comfortable', label: t('density.comfortable') },
              { value: 'compact', label: t('density.compact') },
            ]}
          />
        </div>

        <aside className="prefs__side">
          <section className="prefs__card prefs__card--warm">
            <p className="prefs__eyebrow">{t('language.axes_lead')}</p>
            <ul className="prefs__axes">
              <li>
                <strong>{t('language.interface')}</strong>
              </li>
              <li>{t('language.liturgical')}</li>
              <li>{t('language.export')}</li>
              <li>{t('language.notification')}</li>
            </ul>
            <p className="prefs__fine">{t('language.axes_detail')}</p>
          </section>
        </aside>
      </div>
    </div>
  );
}

function Choice({
  eyebrow,
  value,
  options,
  disabled,
  note,
  onChange,
}: {
  eyebrow: string;
  value: string | undefined;
  options: { value: string; label: string; note?: string }[];
  disabled: boolean;
  note?: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <section className="prefs__card">
      <p className="prefs__eyebrow">{eyebrow}</p>

      <div className="prefs__choices">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            // `aria-pressed` rather than a visual class alone: the selected state has to reach a
            // screen reader, and colour is not a signal it can perceive.
            aria-pressed={value === option.value}
            className={`prefs__choice${value === option.value ? ' prefs__choice--on' : ''}`}
            disabled={disabled}
            onClick={() => onChange(option.value)}
          >
            <span className="prefs__choice-label">{option.label}</span>
            {option.note ? <span className="prefs__choice-note">{option.note}</span> : null}
          </button>
        ))}
      </div>

      {note ? <p className="prefs__fine">{note}</p> : null}
    </section>
  );
}
